import { Button } from '@crehana/compass.ui/v3/button';
import {
  Dialog,
  DialogBanner,
  DialogBody,
  DialogCloseButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@crehana/compass.ui/v3/dialog';
import { useState } from 'react';

import { useGetShiftForDate } from '../../hooks/use-get-shift-for-date';
import { useTranslation } from '../../i18n';
import { isJornadaOpen } from '../../utils';
import { RequestAmountReasonFields } from './components/request-amount-reason-fields';
import { RequestDateShiftFields } from './components/request-date-shift-fields';
import type {
  TCreateRequestBannerCode,
  TCreateRequestFormState,
  TCreateRequestPayload,
} from './types';
import {
  hasFormErrors,
  parseAmountOrZero,
  validateCreateRequestForm,
} from './utils';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    payload: TCreateRequestPayload,
  ) => Promise<TCreateRequestBannerCode | null>;
  submitting: boolean;
};

const BANNER_KEY: Record<
  TCreateRequestBannerCode,
  'VALIDATION_DUPLICATE_REQUEST' | 'VALIDATION_SHIFT_NOT_RESOLVED'
> = {
  duplicate: 'VALIDATION_DUPLICATE_REQUEST',
  shiftRequired: 'VALIDATION_SHIFT_NOT_RESOLVED',
};

const INITIAL_STATE: TCreateRequestFormState = {
  date: undefined,
  shiftId: null,
  overtimeText: '',
  compensationText: '',
  reason: '',
};

export const CreateRequestDialog = ({
  open,
  onOpenChange,
  onSubmit,
  submitting,
}: Props) => {
  const { t } = useTranslation();
  const [state, setState] = useState<TCreateRequestFormState>(INITIAL_STATE);
  const [touched, setTouched] = useState(false);
  const [bannerCode, setBannerCode] = useState<TCreateRequestBannerCode | null>(
    null,
  );

  const {
    shifts,
    loading: shiftLoading,
    error: shiftError,
  } = useGetShiftForDate(state.date);
  const errors = validateCreateRequestForm(state, {
    shifts,
    selectedShiftId: state.shiftId,
    loading: shiftLoading,
    error: shiftError,
  });

  // TIM-560 (Julian, 2026-09-23) — Producto: sin turno no se crea la
  // solicitud. Derivado de los MISMOS errores que validateCreateRequestForm
  // ya calcula para el turno, nunca un booleano paralelo (TIM-559 mostró que
  // dos chequeos separados se desincronizan). Los demás errores (montos,
  // motivo) siguen su flujo touched-on-click, sin deshabilitar el botón.
  const isShiftBlocking =
    Boolean(errors.shift) || Boolean(errors.shiftPending) || !state.date;

  // TIM-663 CAMBIO B/C — the CHOSEN shift is the one whose overtime
  // configuration drives the rounding preview and the compensation field:
  // a single turno is chosen automatically, 2+ turnos wait for a pick.
  const selectedShift =
    shifts?.length === 1
      ? shifts[0]
      : shifts?.find(shift => shift.id === state.shiftId);

  const handleOpenChange = (nextOpen: boolean) => {
    // base-ui's Escape dismiss ignores `dismissible` (it only gates an
    // outside click) — this guard is what actually keeps the dialog open for
    // every dismiss path while the request is in flight. Without it the form
    // resets and the dialog closes, and the mutation still lands: the request
    // is created and the toast fires after the collaborator believed they had
    // cancelled.
    if (!nextOpen && submitting) {
      return;
    }
    if (!nextOpen) {
      setState(INITIAL_STATE);
      setTouched(false);
      setBannerCode(null);
    }
    onOpenChange(nextOpen);
  };

  const handleDateChange = (date: Date | undefined) => {
    setState(s => ({ ...s, date, shiftId: null }));
    setBannerCode(null);
  };

  const handleShiftIdChange = (shiftId: string | null) => {
    setState(s => ({ ...s, shiftId }));
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (hasFormErrors(errors) || !state.date) {
      return;
    }

    const result = await onSubmit({
      date: state.date,
      // CAMBIO A — a request never sends shift_id: null while a turno
      // exists: with 1 turno it is auto-selected, with 2+ Guardar is
      // blocked until one is picked.
      shiftId: selectedShift?.id ?? null,
      extraMinutes: parseAmountOrZero(state.overtimeText) ?? 0,
      replacementMinutes: parseAmountOrZero(state.compensationText) ?? 0,
      reason: state.reason.trim(),
    });
    setBannerCode(result);
  };

  return (
    // compass `Dialog` defaults `dismissible` to true; this blocks an outside
    // click while the request is in flight (`handleOpenChange` above blocks
    // Escape and the X, the two paths `dismissible` does not cover).
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      dismissible={!submitting}
    >
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{t('CREATE_DIALOG_TITLE')}</DialogTitle>
          <DialogCloseButton />
        </DialogHeader>
        {/* TIM-560 (Julian, 2026-09-20) — top banner, sibling of DialogBody,
            not the prototype's bordered card. `DialogBanner` is the DS top
            banner: no border, no radius, full dialog width.
            `[&>svg]:tw-hidden` keeps the original `icon={false}` intent:
            `DialogBanner` renders its variant icon unconditionally (its props
            are only variant | className | children), and Laura ruled on
            2026-08-05 that a dialog top banner carries text only, no icon.
            Same idiom as `edit-workday-overtime-banner.tsx`. */}
        {state.date && isJornadaOpen(state.date) && (
          <DialogBanner variant="info" className="[&>svg]:tw-hidden">
            {t('CREATE_DIALOG_OPEN_DAY_WARNING')}
          </DialogBanner>
        )}
        {bannerCode && (
          <DialogBanner variant="warning" className="[&>svg]:tw-hidden">
            {t(BANNER_KEY[bannerCode])}
          </DialogBanner>
        )}
        <DialogBody>
          <div className="tw-flex tw-flex-col tw-cds-gap-5">
            <RequestDateShiftFields
              date={state.date}
              onDateChange={handleDateChange}
              shifts={shifts}
              shiftLoading={shiftLoading}
              shiftError={shiftError}
              selectedShiftId={state.shiftId}
              onShiftIdChange={handleShiftIdChange}
              showDateError={touched && Boolean(errors.date)}
              showShiftError={touched && Boolean(errors.shift)}
            />
            <RequestAmountReasonFields
              overtimeText={state.overtimeText}
              onOvertimeChange={value =>
                setState(s => ({ ...s, overtimeText: value }))
              }
              compensationText={state.compensationText}
              onCompensationChange={value =>
                setState(s => ({ ...s, compensationText: value }))
              }
              reason={state.reason}
              onReasonChange={value => setState(s => ({ ...s, reason: value }))}
              overtimeConfigActive={
                selectedShift?.overtime_config_active ?? false
              }
              compensationAvailable={
                selectedShift?.compensation_available ?? false
              }
              quantificationType={selectedShift?.quantification_type}
              quantificationTramos={selectedShift?.quantification_tramos ?? []}
              quantificationBlockMinutes={
                selectedShift?.quantification_block_minutes
              }
              showOvertimeFormatError={
                touched && Boolean(errors.overtimeFormat)
              }
              showOvertimeRequiredError={
                touched && Boolean(errors.overtimeRequired)
              }
              showCompensationFormatError={
                touched && Boolean(errors.compensationFormat)
              }
              showCompensationRequiredError={
                touched && Boolean(errors.compensationRequired)
              }
              showAmountsExceedDayError={
                touched && Boolean(errors.amountsExceedDay)
              }
              showReasonRequiredError={touched && Boolean(errors.reason)}
              showReasonMaxLengthError={
                touched && Boolean(errors.reasonMaxLength)
              }
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => handleOpenChange(false)}
          >
            {t('CREATE_DIALOG_CANCEL')}
          </Button>
          <Button
            type="button"
            variant="primary"
            loading={submitting}
            disabled={isShiftBlocking}
            onClick={handleSubmit}
          >
            {t('CREATE_DIALOG_SUBMIT')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
